import { differenceInCalendarDays } from 'date-fns'
import type { PropertyCalendarReview } from '@/api/client'
import { parseCalendarDate } from '@/lib/calendar-date'
import { routes } from '@/lib/routes'
import { localDate } from '@/components/cleaning/presentation'
import { formatSupplyDay } from '@/lib/supply-events'

/**
 * Presentation rules for the calendar's reviews layer.
 *
 * Every day comes from admin-api: the closing day (Airbnb checkout plus 14),
 * the day each side's review landed, and the sentence a chase carries. Nothing
 * here works out a deadline. The functions pick which of the returned days get
 * a marker on the grid, name them, and format them.
 *
 * What the API does NOT know matters as much as what it does. Without a chase
 * record (`has_chase_record` false) the host's review, the status, the
 * reminders and the thank-you are unknown, and an unknown is never drawn as a
 * "no": the host's review has no marker then, and the stay sheet says "not
 * tracked". The guest's review day does not depend on the chase.
 */

export type ReviewMarkerKind = 'closes' | 'guest' | 'host'

/** One marker on the grid: a review event of one stay, on its property-local day. */
export interface ReviewMarker {
  /** Stable and unique across stays and kinds, for React. */
  key: string
  reservationId: string
  kind: ReviewMarkerKind
  /** Property-local `YYYY-MM-DD`, as the API returned it. */
  day: string
  /** The window has not closed yet. A review that has landed is never planned. */
  planned: boolean
  /** The window is open and the chase says your review is still outstanding: worth a look. */
  owed: boolean
  /** The guest, when the month's stays name them; the day list's second line. */
  detail: string | null
}

/**
 * Where a review window stands on `today`: `upcoming` (the guest has not checked
 * out, so nothing can be reviewed yet), `open`, or `closed` (the closing day is
 * past). All three days are the API's: `today` is the day of the read's own
 * `generated_at` in the property's zone, so it is the server's clock and not the
 * browser's. The checkout day and the closing day themselves are still the
 * stay's and the window's. Null when `today` could not be read or the channel
 * has no deadline, because neither can be said.
 */
export type ReviewWindowState = 'upcoming' | 'open' | 'closed'

export function reviewWindowState(
  review: Pick<PropertyCalendarReview, 'check_out' | 'window_closes_on'>,
  today: string | null
): ReviewWindowState | null {
  const closes = review.window_closes_on
  if (today === null || closes === null) return null
  if (closes < today) return 'closed'
  return review.check_out !== null && review.check_out > today ? 'upcoming' : 'open'
}

/**
 * Whether you still owe this stay a review: the window is open, the guest has
 * checked out, and a chase record says the host has not reviewed. One rule for
 * the amber "Review window closes" block and the "Reviews to leave" list, so
 * the two never disagree. Without a chase record it is not known, so it is
 * never "owed".
 */
export function isReviewOwed(review: PropertyCalendarReview, today: string | null): boolean {
  return (
    reviewWindowState(review, today) === 'open' &&
    review.has_chase_record &&
    review.status === 'pending' &&
    !review.host_reviewed_on &&
    !review.host_reviewed_at
  )
}

/** How a stay is on the "Reviews to leave" list. */
export type ReviewToLeaveKind =
  /** Window open and the chase says you have not reviewed. The only kind that is counted. */
  | 'owed'
  /** Window open, the guest's review is in, and the chase says no thank-you went out. */
  | 'thank_you'
  /** Window open but no chase record: whether you reviewed is unknown. Never counted. */
  | 'untracked'

export interface ReviewToLeave {
  kind: ReviewToLeaveKind
  review: PropertyCalendarReview
  /** Owed, and the guest has reviewed with no thank-you yet: said on the same row. */
  thankYouPending: boolean
}

export interface ReviewsToLeave {
  owed: ReviewToLeave[]
  thankYou: ReviewToLeave[]
  untracked: ReviewToLeave[]
}

/**
 * The stays that belong on the "Reviews to leave" list, split three ways and
 * each sorted by the day the window closes, soonest first (the API's stay
 * order within a day). Only windows that are open on `today` are listed: a
 * closed one has nothing left to chase, and a stay whose guest has not checked
 * out has nothing to review yet.
 *
 * A stay lands in exactly one group, owed first. A thank-you is pending only
 * where the chase is known (`has_chase_record`) and the guest's review is in.
 */
export function buildReviewsToLeave(
  reviews: PropertyCalendarReview[],
  today: string | null
): ReviewsToLeave {
  const out: ReviewsToLeave = { owed: [], thankYou: [], untracked: [] }
  for (const review of reviews) {
    if (reviewWindowState(review, today) !== 'open') continue
    const thankYouPending =
      review.has_chase_record && !!review.guest_reviewed_on && !review.thank_you_sent_at
    if (isReviewOwed(review, today)) out.owed.push({ kind: 'owed', review, thankYouPending })
    else if (!review.has_chase_record)
      out.untracked.push({ kind: 'untracked', review, thankYouPending: false })
    else if (thankYouPending) out.thankYou.push({ kind: 'thank_you', review, thankYouPending })
  }
  const byClosing = (a: ReviewToLeave, b: ReviewToLeave) =>
    (a.review.window_closes_on ?? '').localeCompare(b.review.window_closes_on ?? '')
  // Array.prototype.sort is stable, so the API's order breaks ties.
  out.owed.sort(byClosing)
  out.thankYou.sort(byClosing)
  out.untracked.sort(byClosing)
  return out
}

/**
 * "Closes today", "Closes tomorrow", "Closes in 5 days": the API's closing day
 * against the day of the read's own `generated_at`, the same two days
 * `reviewWindowState` compares. Null when either is missing or the window has
 * closed.
 */
export function closesInWords(closesOn: string | null, today: string | null): string | null {
  const closes = parseCalendarDate(closesOn)
  const now = parseCalendarDate(today)
  if (!closes || !now) return null
  const days = differenceInCalendarDays(closes, now)
  if (days < 0) return null
  return days === 0 ? 'Closes today' : days === 1 ? 'Closes tomorrow' : `Closes in ${days} days`
}

/** "Marina Shapiro - checked out Jul 7"; whichever half is known. */
function markerDetail(guest: string | null, checkOut: string | null): string | null {
  const out = checkOut ? `checked out ${formatSupplyDay(checkOut)}` : null
  return [guest, out].filter(Boolean).join(' - ') || null
}

/**
 * The markers for the given reviews, restricted to the days `inDay` accepts
 * (the visible month: a stay is listed for any month it touches, and its
 * window or a review can fall in another). Earliest day first; on one day the
 * window closing comes before the reviews, then the API's stay order.
 */
export function buildReviewMarkers(
  reviews: PropertyCalendarReview[],
  {
    today,
    guestNames,
    inDay,
  }: {
    today: string | null
    /** Reservation id to guest name, from the same read's stays. */
    guestNames?: ReadonlyMap<string, string | null>
    inDay: (day: string) => boolean
  }
): ReviewMarker[] {
  const markers: ReviewMarker[] = []
  for (const review of reviews) {
    const id = review.reservation_id
    const detail = markerDetail(guestNames?.get(id) ?? null, review.check_out)
    const add = (kind: ReviewMarkerKind, day: string | null, planned: boolean, owed: boolean) => {
      if (!day || !inDay(day)) return
      markers.push({
        key: `review:${id}:${kind}`,
        reservationId: id,
        kind,
        day,
        planned,
        owed,
        detail,
      })
    }

    const state = reviewWindowState(review, today)
    // "Closes" claims less than "closed": an unreadable day leaves the window open.
    const planned = state !== 'closed'
    const owed = isReviewOwed(review, today)
    add('closes', review.window_closes_on, planned, owed)
    add('guest', review.guest_reviewed_on, false, false)
    add('host', review.host_reviewed_on, false, false)
  }
  const order: Record<ReviewMarkerKind, number> = { closes: 0, guest: 1, host: 2 }
  return markers.sort((a, b) => a.day.localeCompare(b.day) || order[a.kind] - order[b.kind])
}

export function groupReviewMarkersByDay(markers: ReviewMarker[]): Map<string, ReviewMarker[]> {
  const byDay = new Map<string, ReviewMarker[]>()
  for (const marker of markers) {
    const list = byDay.get(marker.day)
    if (list) list.push(marker)
    else byDay.set(marker.day, [marker])
  }
  return byDay
}

/** The server's "today" in the property's zone: the read's own `generated_at`, never the browser's clock. */
export function reviewToday(
  generatedAt: string | null | undefined,
  timeZone: string
): string | null {
  return localDate(generatedAt, timeZone)
}

/** What the chase's status means, in words. A status this build has not met is shown as the API named it. */
export function chaseStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'Waiting for reviews'
    case 'review_received':
      return 'Review received'
    case 'skipped':
      return 'Skipped'
    case 'expired':
      return 'Expired'
    default:
      return status.replace(/_/g, ' ')
  }
}

/** "Jul 12" for each instant, in the property's zone, oldest first as given. */
export function formatInstantDays(instants: string[], timeZone: string): string {
  return instants
    .map(instant => {
      const day = localDate(instant, timeZone)
      return day ? formatSupplyDay(day, { withYear: true }) : instant
    })
    .join(', ')
}

/** The Reviews page's `?reservation=<id>`: scroll to and highlight that stay's row. */
export const REVIEW_ROW_PARAM = 'reservation'

/** The Reviews page, scrolled to one stay's row. Navigation only: it opens and sends nothing. */
export function reviewRowHref(reservationId: string): string {
  return `${routes.reviews.path}?${REVIEW_ROW_PARAM}=${encodeURIComponent(reservationId)}`
}
